import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// Attain button system (RFC 0050 · foundations → Buttons).
// ONE radius, --radius-button (4px unless the app makes buttons square,
// System/Shape), for every button — the AssistantChatButton reference.
// `shape="square"` squares one button whatever the app's default. THREE colour variants — `primary` (brand burgundy), `secondary`
// (neutral grey) and `quiet` (transparent, for an action beside content) — plus a text `link`. THREE heights — small 24 / default 32 /
// prominent 40 (max). Heights are literal `h-[Npx]` because the app sets
// `html { font-size: 13px }`, so rem `h-8` would resolve to 26px, not 32.
// (`compact`/`medium`/`responsive`/`tertiary` were removed post-migration; `small`
// was promoted to canonical for dense inline controls.)
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-ds-2 font-ds-sans leading-[1.4] whitespace-nowrap rounded-[var(--radius-button)] ' +
    'transition-[background-color,border-color,color] duration-150 cursor-pointer ' +
    'disabled:cursor-not-allowed disabled:opacity-50 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        // Brand burgundy — the single high-emphasis action.
        primary: 'bg-ds-accent text-white border border-ds-accent hover:bg-ds-accent-hover',
        // Neutral grey — low-emphasis; never competes with primary.
        secondary:
          'bg-ds-paper text-ds-text border border-ds-border hover:bg-ds-grey-light active:bg-ds-grey-mid',
        // Quiet: transparent at rest, in the secondary ink, for an action that
        // must not compete with the content it sits in (the comments' Edit,
        // Reply, Resolve). Ink and the 7% fill on hover, 11% pressed
        // (--color-bg-hover, --color-bg-active). The one place small text
        // rests in the secondary ink: the plan's documented exception.
        quiet:
          'bg-transparent text-ds-text-secondary border-none hover:text-ds-text hover:bg-ds-hover active:bg-ds-active',
        // @deprecated 3 Oct 2026. A link is the Link component
        // (components/Link, `as="button"` when it acts in place), never a
        // Button. Kept only until its last consumer moves: develop's
        // enterprise/GuestAccessPanel (the Cancel beside Confirm). Do not use.
        link: 'bg-transparent text-ds-accent border-none p-0 underline underline-offset-2 hover:text-ds-accent-hover',
      },
      size: {
        // Dense inline controls — pagination, inline chart links, toolbar actions. 24px.
        small: 'h-[24px] px-3 text-[12px] font-medium',
        // The standard — nearly everything. 32px.
        default: 'h-[32px] px-4 text-[13px] font-semibold',
        // Max (40px) — major page actions, full-width form submits. Used sparingly.
        prominent: 'h-[40px] px-5 text-[14px] font-semibold',
      },
      shape: {
        // The app's button radius (--radius-button).
        default: '',
        // Square whatever the app's default.
        square: 'rounded-none',
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        className: '!p-0',
      },
      // A quiet action is tight: 8px at the sides and 4px to its icon, as the
      // comments drew it, so moving them onto Button shifted nothing. The gap
      // is marked important because twMerge does not know gap-ds-2 is a gap.
      {
        variant: 'quiet',
        className: 'px-[8px] !gap-[4px]',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      shape: 'default',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  children: ReactNode;
  /** Render as the child element (Radix Slot) — e.g. to wrap a router `<Link>`. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, shape, className, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, shape }), className)}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { buttonVariants };
